import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Mail, MessageSquare, LogOut, ShieldCheck } from "lucide-react";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "doryium_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("members");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [merch, setMerch] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) tryAuth(token);
  }, []);

  const tryAuth = async (t) => {
    setLoading(true);
    try {
      const s = await axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Token": t } });
      setStats(s.data);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      loadAll(t);
    } catch {
      setAuthed(false);
      localStorage.removeItem(TOKEN_KEY);
      toast.error("Invalid admin token");
    } finally {
      setLoading(false);
    }
  };

  const loadAll = async (t) => {
    const h = { headers: { "X-Admin-Token": t } };
    try {
      const [m, s, c] = await Promise.all([
        axios.get(`${API}/community/members`, h),
        axios.get(`${API}/merch/subscribers`, h),
        axios.get(`${API}/contact/messages`, h),
      ]);
      setMembers(m.data);
      setMerch(s.data);
      setMessages(c.data);
    } catch {
      toast.error("Failed to load data");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="panel panel-gold p-8 w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-6 h-6 text-[#ffd977]" />
            <h1 className="font-display text-2xl gold-text">Admin Access</h1>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              tryAuth(input.trim());
            }}
          >
            <Label className="text-white/70 text-xs uppercase tracking-widest font-heading">
              Admin Token
            </Label>
            <input
              type="password"
              className="doryium-input mt-2"
              placeholder="Enter admin token"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="admin-token-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-gold mt-6 w-full rounded-xl py-3 font-heading uppercase tracking-widest text-sm disabled:opacity-70"
              data-testid="admin-login-btn"
            >
              {loading ? "Verifying..." : "Enter Dashboard"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="font-display gold-text tracking-widest text-xl">DORYIUM • ADMIN</div>
          <button
            onClick={logout}
            className="btn-outline-gold rounded-lg px-4 py-2 text-xs font-heading uppercase tracking-widest flex items-center gap-2"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-5">
        <div className="grid grid-cols-3 gap-4 mb-8">
          <StatBox icon={<Users className="w-5 h-5" />} label="Members" value={stats?.members ?? 0} />
          <StatBox icon={<Mail className="w-5 h-5" />} label="Merch Waitlist" value={stats?.merch_subscribers ?? 0} />
          <StatBox icon={<MessageSquare className="w-5 h-5" />} label="Messages" value={stats?.contact_messages ?? 0} />
        </div>

        <div className="flex gap-2 mb-4">
          <TabBtn active={tab === "members"} onClick={() => setTab("members")} testid="tab-members">Members</TabBtn>
          <TabBtn active={tab === "merch"} onClick={() => setTab("merch")} testid="tab-merch">Merch</TabBtn>
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testid="tab-messages">Messages</TabBtn>
        </div>

        {tab === "members" && (
          <Table
            columns={["Name", "Email", "Wallet", "Joined"]}
            rows={members.map((m) => [
              m.name,
              m.email,
              m.wallet || "—",
              new Date(m.created_at).toLocaleString(),
            ])}
            emptyMsg="No members yet"
            testid="members-table"
          />
        )}
        {tab === "merch" && (
          <Table
            columns={["Email", "Signed Up"]}
            rows={merch.map((m) => [m.email, new Date(m.created_at).toLocaleString()])}
            emptyMsg="No merch subscribers yet"
            testid="merch-table"
          />
        )}
        {tab === "messages" && (
          <Table
            columns={["Name", "Email", "Message", "When"]}
            rows={messages.map((m) => [
              m.name,
              m.email,
              m.message,
              new Date(m.created_at).toLocaleString(),
            ])}
            emptyMsg="No messages yet"
            testid="messages-table"
          />
        )}
      </main>
    </div>
  );
}

function StatBox({ icon, label, value }) {
  return (
    <div className="panel p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#f5c65a]/15 text-[#ffd977] grid place-items-center">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-heading">{label}</div>
        <div className="font-display text-2xl text-white">{value}</div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-xs font-heading uppercase tracking-widest transition-all ${
        active ? "btn-gold" : "btn-outline-gold"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function Table({ columns, rows, emptyMsg, testid }) {
  if (!rows.length) {
    return (
      <div className="panel p-10 text-center text-white/50" data-testid={`${testid}-empty`}>
        {emptyMsg}
      </div>
    );
  }
  return (
    <div className="panel overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-white/10">
            {columns.map((c) => (
              <th key={c} className="px-4 py-3 text-[10px] uppercase tracking-widest text-white/50 font-heading">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-white/80 align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
